import React from 'react';
import { theme } from '../../../../themes';
import { Typography } from '@mui/material';
import { MUIDataTableOptions } from 'mui-datatables';
import { StrategyAssociationItem } from '../store/types';
import { LoadingProgress } from '../../../../common/store/types';
import { formatDollars, formatNumberCommaSeparated } from '../../../../common';
import { DatatableColumn, FilterDataType } from '../../../../common/components/dataTable/types';
import { ClientSideDataTable } from '../../../../common/components/dataTable/clientSide/clientSideDataTable';

export interface Props {
  progress: LoadingProgress;
  data: StrategyAssociationItem[];
}
export function AssociationsTable({ progress, data }: Props): JSX.Element {
  const defaultItems: StrategyAssociationItem[] = [];
  const columns: DatatableColumn[] = [
    {
      label: 'AFSL NAME',
      name: 'afslName',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <Typography
              variant={'h5'}
              color={'primary'}
              style={{
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                letterSpacing: '1px',
              }}
            >
              {data[dataIndex] !== undefined ? data[dataIndex].afslName : ''}
            </Typography>
          );
        },
        sort: false,
        viewColumns: false,
      },
    },
    {
      label: 'PRACTICE NAME',  
      name: 'practiceName',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'ADVISER NAME',  
      name: 'adviserName',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'CLIENT ID',
      name: 'clientId',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <Typography
              variant={'h5'}
              color={'primary'}
              style={{
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                letterSpacing: '1px',
              }}
            >
              {data[dataIndex] !== undefined ? data[dataIndex].clientId : ''}
            </Typography>
          );
        },
        sort: false,
        viewColumns: false,
      },
    },
    {
      label: 'CLIENT NAME',  
      name: 'clientName',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'PORTFOLIO ID',
      name: 'investmentServiceCode',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <Typography
              variant={'h5'}
              color={'primary'}
              style={{
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                letterSpacing: '1px',
              }}
            >
              {data[dataIndex] !== undefined ? data[dataIndex]?.investmentServiceCode : ''}
            </Typography>
          );
        },
        sort: false,
        viewColumns: false,
      },
    },
    {
      label: 'PORTFOLIO NAME',  
      name: 'portfolioName',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'CLIENT NAME',
      name: 'clientName',
      textAlign: 'left',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <Typography variant={'h5'} align="left">
              {data[dataIndex] !== undefined ? data[dataIndex]?.clientName : ''}
            </Typography>
          );
        },
        download: false,  
        sort: true,
      },
      filterDataType: FilterDataType.string,
    },
    {
      label: 'PORTFOLIO NAME',
      name: 'portfolioName',
      textAlign: 'left',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <Typography variant={'h5'} align="left">
              {data[dataIndex] !== undefined ? data[dataIndex]?.investmentServiceName : ''}
            </Typography>
          );
        },
        download: false, 
        sort: true,
      },
      filterDataType: FilterDataType.string,
    },
    {
      label: 'ACCOUNT NUMBER',
      name: 'accountNumber',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <Typography
              variant={'h5'}
              color={'primary'}
              style={{
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                letterSpacing: '1px',
              }}
            >
              {data[dataIndex] !== undefined ? data[dataIndex].accountNumber : ''}
            </Typography>
          );
        },
        sort: false,
        viewColumns: false,
      },
    },
    {
      label: 'STRATEGY CODE',
      name: 'strategyCode',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <Typography
              variant={'h5'}
              color={'primary'}
              style={{
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                letterSpacing: '1px',
              }}
            >
              {data[dataIndex] !== undefined ? data[dataIndex].strategyCode : ''}
            </Typography>
          );
        },
        download: false,     
        sort: false,
        viewColumns: false,
      },
    },
    {
      label: 'ACCOUNT NAME',  
      name: 'accountName',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'PRICE',  
      name: 'price',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'UNITS',  
      name: 'units',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'UNITS & PRICE',
      name: 'units',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <>
              <Typography variant="h5">{formatNumberCommaSeparated(data[dataIndex]?.units ?? 0, 4)}</Typography>
              <Typography color={'textSecondary'} variant={'h6'}>
                {formatDollars(data[dataIndex]?.unitPrice, false, 4)}
              </Typography>
            </>
          );
        },
        download: false,
        sort: false,
        viewColumns: false,
      },
    },
    {
      filterDataType: FilterDataType.string,
      textAlign: 'right',
      label: 'VALUE',
      name: 'value',
      options: {
         customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
            return <Typography variant="h5">{formatDollars(data[dataIndex]?.value)}</Typography>;
         },
         sort: false,
        viewColumns: false,
      },
      },
  ];
  
  const options: MUIDataTableOptions = {
    search: true,
    download: true,
    filter: false,
  };

  const tableData = (data && data.length > 0 && !progress.isLoading ? data : defaultItems).map(item => ({
    ...item,
    practiceName: item.practiceName,
    adviserName: item.adviserName,
    clientName: item.clientName,
    portfolioName: item.investmentServiceName,
    accountName: item.accountName,
    unitPrice: item.unitPrice,
    units: item.units,
    afslName: item.afslName,
    value: item.value
  }));

  return (
    <ClientSideDataTable
      data={tableData}
      columns={columns}
      options={options}
      loadingProgress={progress}
    />
  );
}
